import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DiagnosticsRepository } from '../../../../infrastructure/http/repositories/diagnostics.repository';
import { DialogService } from '../../../../core/services/dialog.service';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { DiagnosticStuckRequestsComponent } from '../diagnostic-stuck-requests/diagnostic-stuck-requests.component';
import {
  InfraCategory,
  type InfrastructureCheck,
  type SystemHealth,
  InfraCategoryNameMap,
  StuckRequest,
} from '../../models/diagnostics.models';
import { AuthService } from 'src/app/core';

@Component({
  selector: 'app-diagnostic-system-health-tab',
  standalone: true,
  imports: [
    CommonModule,
    DiagnosticStuckRequestsComponent,
    ConfirmDialogComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './diagnostic-system-health-tab.component.html',
  styleUrls: ['./diagnostic-system-health-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosticSystemHealthTabComponent implements OnInit {
  private readonly repo = inject(DiagnosticsRepository);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialogService = inject(DialogService);
  private readonly authService = inject(AuthService);
  private currentUser: string | undefined;

  ngOnInit(): void {
    this.authService.currentUser$.subscribe(user => {
      this.currentUser = user?.fullName ?? 'DiagnosticsPage';
    });
    this.loadSystemHealth();
  }

  readonly InfraCategory = InfraCategory;

  stuckPage = 1;
  stuckPageSize = 10;

  stuckRequestsConfig: { showSection: boolean; loading: boolean } = {
    showSection: false,
    loading: true,
  };
  rejectedFilesConfig: { showSection: boolean; loading: boolean } = {
    showSection: false,
    loading: true,
  };
  windowsHealthCheckConfig: { showSection: boolean; loading: boolean } = {
    showSection: false,
    loading: true,
  };
  databaseConnectivityConfig: { showSection: boolean; loading: boolean } = {
    showSection: false,
    loading: true,
  };
  objectStorageHealthConfig: { showSection: boolean; loading: boolean } = {
    showSection: false,
    loading: true,
  };
  sftpHealthConfig: { showSection: boolean; loading: boolean } = {
    showSection: false,
    loading: true,
  };
  authEndpointConfig: { showSection: boolean; loading: boolean } = {
    showSection: false,
    loading: true,
  };
  smtpConfig: { showSection: boolean; loading: boolean } = {
    showSection: false,
    loading: true,
  };

  isLoadingHealth = false;
  systemHealth: SystemHealth = {
    stuckRequests: {
      isVisibleOnGridCount: 0,
      isProcessingCount: 0,
      isReady2ProcessCount: 0,
      isReady4UluroCount: 0,
      details: [] as StuckRequest[],
      totalCount: 0,
      pageNumber: 0,
      pageSize: 0,
      totalPages: 0,
    },
    rejectedFiles: [],
    serviceHeartbeats: [],
    infrastructureChecks: [],
  };
  stuckRequests: SystemHealth | null = null;
  rejectedFiles: SystemHealth | null = null;
  windowsHealth: SystemHealth | null = null;
  databaseHealth: InfrastructureCheck[] | null = null;
  objectStorageHealth: InfrastructureCheck[] | null = null;
  sftpHealth: InfrastructureCheck[] | null = null;
  authEndpointHealth: InfrastructureCheck[] | null = null;
  smtpHealth: InfrastructureCheck[] | null = null;
  infraResult: SystemHealth | null = null;
  healthError: string | null = null;
  lastRefreshed: Date | null = null;

  loadSystemHealth(): void {
    this.healthError = null;
    this.cdr.markForCheck();

    this.getStuckRequests(this.stuckPage, this.stuckPageSize);
    this.getRejectedFiles();
    this.getWindowsServiceHeartbeats();
    this.refreshInfraCategories([
      InfraCategory.Database,
      InfraCategory.ObjectStorage,
      InfraCategory.Email,
      InfraCategory.External,
    ]);
    this.refreshInfraCategories([InfraCategory.Sftp]);
  }

  onStuckPageChange(page: number): void {
    this.stuckPage = page;
    this.getStuckRequests(this.stuckPage, this.stuckPageSize);
  }

  onStuckPageSizeChange(event: Event): void {
    const size = parseInt((event.target as HTMLSelectElement).value, 10);
    this.stuckPageSize = size;
    this.stuckPage = 1;
    this.getStuckRequests(this.stuckPage, this.stuckPageSize);
  }

  onFixVisibilityBulk(): void {
    this.dialogService
      .confirm('Fix visibility for ALL stuck requests?')
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.stuckRequestsConfig.loading = true;
        this.cdr.markForCheck();
        this.repo.fixVisibility().subscribe(result => {
          this.stuckPage = 1;
          this.getStuckRequests(this.stuckPage, this.stuckPageSize);
          this.dialogService
            .alert(`Fixed ${result.fixedCount} requests.`)
            .subscribe();
        });
      });
  }

  onFixVisibilityForRequest(requestId: number): void {
    this.dialogService
      .confirm(`Fix visibility for request ${requestId}?`)
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.stuckRequestsConfig.loading = true;
        this.cdr.markForCheck();
        this.repo.fixVisibility(requestId).subscribe(() => {
          this.getStuckRequests(this.stuckPage, this.stuckPageSize);
        });
      });
  }

  onReleaseProcessingLockForRequest(requestId: number): void {
    this.dialogService
      .confirm(`Release processing lock for request ${requestId}?`)
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.stuckRequestsConfig.loading = true;
        this.cdr.markForCheck();
        this.repo.releaseProcessingLock(requestId).subscribe(() => {
          this.getStuckRequests(this.stuckPage, this.stuckPageSize);
        });
      });
  }

  onMarkFileReadyForStuck(fileId: number): void {
    this.dialogService
      .confirm(
        `Mark file ${fileId} as ready to process?\n\nOnly safe if the file exists in Object Storage.`
      )
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.stuckRequestsConfig.loading = true;
        this.cdr.markForCheck();
        this.repo
          .markFileReady(fileId.toString(), this.currentUser!)
          .subscribe(() => {
            this.getStuckRequests(this.stuckPage, this.stuckPageSize);
          });
      });
  }

  infraChecks(category: string): InfrastructureCheck[] {
    return (
      this.infraResult?.infrastructureChecks.filter(
        c => c.category === category
      ) ?? []
    );
  }

  infraStatusClass(status: string): string {
    switch (status) {
      case 'Connected':
      case 'Authenticated':
      case 'Reachable':
        return 'hs-ok';
      case 'Unreachable':
      case 'Error':
        return 'hs-down';
      default:
        return '';
    }
  }

  formatAge(minutes: number): string {
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    const mins = minutes % 60;
    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0 || parts.length === 0) parts.push(`${mins}m`);
    return parts.join(' ');
  }

  getStuckRequests(page: number, size: number) {
    this.stuckRequestsConfig.loading = true;
    this.stuckRequestsConfig.showSection = true;

    this.repo.getSystemHealth(page, size).subscribe({
      next: result => {
        this.isLoadingHealth = false;
        this.stuckRequestsConfig.loading = false;
        if (result) {
          if (result.stuckRequests.details.length === 0 && this.stuckPage > 1) {
            this.stuckPage = this.stuckPage - 1;
            this.getStuckRequests(this.stuckPage, this.stuckPageSize);
            return;
          }
          this.systemHealth = result;
          this.lastRefreshed = new Date();
        } else {
          this.healthError = 'Failed to load system health.';
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoadingHealth = false;
        this.stuckRequestsConfig.loading = false;
        this.healthError = 'Failed to load system health.';
        this.cdr.markForCheck();
      },
    });
  }

  getRejectedFiles() {
    this.rejectedFilesConfig.loading = true;
    this.rejectedFilesConfig.showSection = true;

    this.repo.getRejectedFiles().subscribe({
      next: result => {
        this.rejectedFilesConfig.loading = false;
        if (result) {
          this.rejectedFiles = result;
          this.lastRefreshed = new Date();
        } else {
          this.healthError = 'Failed to load system health.';
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.rejectedFilesConfig.loading = false;
        this.healthError = 'Failed to load rejected files.';
        this.cdr.markForCheck();
      },
    });
  }

  getWindowsServiceHeartbeats() {
    this.windowsHealthCheckConfig.loading = true;
    this.windowsHealthCheckConfig.showSection = true;

    this.repo.getServiceHeartbeat().subscribe({
      next: result => {
        this.windowsHealthCheckConfig.loading = false;
        if (result) {
          this.windowsHealth = result;
          this.lastRefreshed = new Date();
        } else {
          this.healthError = 'Failed to load system health.';
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.windowsHealthCheckConfig.loading = false;
        this.healthError = 'Failed to load service heartbeats.';
        this.cdr.markForCheck();
      },
    });
  }

  refresh(category: InfraCategory): void {
    this.refreshInfraCategories([category]);
  }

  private readonly infraCategoryConfigMap: Record<
    InfraCategory,
    { loading: boolean; showSection: boolean }
  > = {
    [InfraCategory.Database]: this.databaseConnectivityConfig,
    [InfraCategory.ObjectStorage]: this.objectStorageHealthConfig,
    [InfraCategory.Sftp]: this.sftpHealthConfig,
    [InfraCategory.External]: this.authEndpointConfig,
    [InfraCategory.Email]: this.smtpConfig,
  };

  private refreshInfraCategories(categories: InfraCategory[]): void {
    for (const category of categories) {
      const config = this.infraCategoryConfigMap[category];
      config.loading = true;
      config.showSection = true;
    }

    const names = categories.flatMap(c => InfraCategoryNameMap[c]).join(',');

    this.repo.getInfraCheck(names).subscribe({
      next: result => {
        for (const category of categories) {
          this.infraCategoryConfigMap[category].loading = false;
        }
        if (result) {
          this.mergeInfraResult(result, categories);
          this.lastRefreshed = new Date();
        } else {
          this.healthError = 'Failed to load infrastructure checks.';
        }
        this.cdr.markForCheck();
      },
      error: () => {
        for (const category of categories) {
          this.infraCategoryConfigMap[category].loading = false;
        }
        this.healthError = 'Failed to load infrastructure checks.';
        this.cdr.markForCheck();
      },
    });
  }

  private mergeInfraResult(
    fresh: SystemHealth,
    categories: InfraCategory[]
  ): void {
    if (!this.infraResult) {
      this.infraResult = fresh;
      return;
    }
    const refreshed = new Set(categories.map(c => InfraCategory[c]));

    this.infraResult = {
      ...this.infraResult,
      infrastructureChecks: [
        ...this.infraResult.infrastructureChecks.filter(
          c => !refreshed.has(c.category as InfraCategory)
        ),
        ...fresh.infrastructureChecks.filter(c =>
          refreshed.has(c.category as InfraCategory)
        ),
      ],
    };
  }
}
